import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, IMG } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Sparkles, ShieldCheck, Pencil, Zap, Star, BadgeCheck, Rocket, Eye, TrendingUp, Check } from "lucide-react";
import { toast } from "sonner";
import VerificationCard from "@/components/VerificationCard";
import BoostDialog from "@/components/BoostDialog";
import ReferralCard from "@/components/ReferralCard";
import SavedSearchesPanel from "@/components/SavedSearchesPanel";
import VisitRequestsPanel from "@/components/VisitRequestsPanel";

export default function DashboardPage() {
  const { user, config } = useAuth();
  const nav = useNavigate();
  const boostOn = !!config?.boost_enabled;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boostTarget, setBoostTarget] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/properties/mine").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try { await api.delete(`/properties/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const revertAi = async (id) => {
    if (!window.confirm("Revert to your original title and description?")) return;
    try { await api.post(`/properties/${id}/normalize/revert`); toast.success("Reverted to your original"); load(); }
    catch { toast.error("Could not revert"); }
  };


  const formatFeaturedUntil = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return null; }
  };

  if (!user) return null;
  const remainingFree = Math.max(0, (user.free_reveals_limit || 0) - (user.free_reveals_used || 0));

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="label-eyebrow text-gray-500 mb-2">Owner dashboard</p>
          <h1 className="font-display text-4xl sm:text-5xl brand-forest flex items-center gap-3">
            Hi, {user.name}
            {user.verification_status === "verified" && (
              <BadgeCheck size={32} className="text-emerald-600" data-testid="header-verified-tick" />
            )}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {user.role === "admin" && (
            <Button
              onClick={() => nav("/admin")}
              variant="outline"
              className="rounded-full h-11 px-4 border-brand-subtle"
              data-testid="admin-verifications-link"
            >
              <ShieldCheck size={16} className="mr-1" /> Admin
            </Button>
          )}
          <Button onClick={() => nav("/list-property")} data-testid="add-listing-btn" className="tlr-gold-btn rounded-full h-11 px-5">
            <Plus size={16} className="mr-1" /> New listing
          </Button>
        </div>
      </div>

      {/* Verification */}
      <div className="mb-6"><VerificationCard /></div>
      <div className="mb-6"><ReferralCard /></div>
      <div className="mb-6"><VisitRequestsPanel /></div>
      <SavedSearchesPanel />

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="bg-white border border-brand-subtle rounded-2xl p-6">
          <p className="label-eyebrow text-gray-500 mb-2">Your listings</p>
          <p className="font-display text-4xl brand-forest" data-testid="stat-listings">{items.length}</p>
        </div>
        <div className="bg-white border border-brand-subtle rounded-2xl p-6">
          <p className="label-eyebrow text-gray-500 mb-2 flex items-center gap-1"><ShieldCheck size={12} /> Free reveals left</p>
          <p className="font-display text-4xl brand-forest" data-testid="stat-free">{remainingFree}<span className="text-lg text-gray-500">/{user.free_reveals_limit}</span></p>
        </div>
        <div className="bg-white border border-brand-subtle rounded-2xl p-6">
          <p className="label-eyebrow text-gray-500 mb-2 flex items-center gap-1"><Sparkles size={12} className="brand-terracotta" /> Paid credits</p>
          <p className="font-display text-4xl brand-forest" data-testid="stat-credits">
            {user.is_unlimited ? "∞" : user.contacts_remaining}
          </p>
          {!user.is_unlimited && remainingFree === 0 && user.contacts_remaining === 0 && (
            <Link to="/pricing" className="text-sm brand-terracotta font-semibold hover:underline mt-1 inline-block">Upgrade →</Link>
          )}
        </div>
      </div>

      <h2 className="font-display text-2xl brand-forest mb-6">Your properties</h2>
      {loading ? (
        <div className="text-center py-20 text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-brand-subtle">
          <p className="text-gray-500 mb-4">You haven't listed any properties yet.</p>
          <Button onClick={() => nav("/list-property")} className="tlr-gold-btn rounded-full h-11 px-6">Create your first listing</Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-brand-subtle rounded-2xl p-4" data-testid="my-listing">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
              <img
                src={p.image_paths?.[0] ? IMG(p.image_paths[0]) : "https://images.pexels.com/photos/16110999/pexels-photo-16110999.jpeg?w=200"}
                alt="" className="w-full md:w-24 h-40 md:h-24 object-cover rounded-xl"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="font-semibold text-gray-900 line-clamp-1">{p.title}</p>
                  {p.moderation_status === "approved" && (
                    <span data-testid={`live-badge-${p.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold tracking-widest uppercase">
                      <Check size={10} /> Live now
                    </span>
                  )}
                  {p.moderation_status === "pending" && (
                    <span data-testid={`pending-badge-${p.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold tracking-widest uppercase">
                      Pending review
                    </span>
                  )}
                  {p.moderation_status === "rejected" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold tracking-widest uppercase">
                      Rejected
                    </span>
                  )}
                  {p.is_featured && (
                    <span data-testid="featured-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0EA5E9] text-white text-[10px] font-bold tracking-widest uppercase">
                      <Star size={10} className="fill-white" /> Featured
                    </span>
                  )}
                  {p.meta_title && (
                    <span data-testid={`ai-optimised-${p.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F3EEE4] text-[#7A1E2A] text-[10px] font-bold tracking-widest uppercase">
                      <Sparkles size={10} /> AI-optimised
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 line-clamp-1">{p.locality}, {p.city} • {p.bhk} BHK • {p.area_sqft} sqft</p>
                <p className="font-display text-lg brand-forest mt-1">
                  ₹{p.price.toLocaleString("en-IN")}{p.listing_type === "rent" ? "/mo" : ""}
                </p>
                {p.is_featured && p.featured_until && (
                  <p className="text-[11px] text-[#0EA5E9] mt-1 font-semibold">Featured until {formatFeaturedUntil(p.featured_until)}</p>
                )}
                {p.moderation_status === "pending" && p.auto_review?.checks?.some((c) => c.status === "fail") && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`review-flags-${p.id}`}>
                    <span className="text-[11px] text-gray-500">Being reviewed for:</span>
                    {p.auto_review.checks.filter((c) => c.status === "fail").map((c) => (
                      <span key={c.name} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" title={c.detail}>
                        {c.name === "photos" ? "photos" : c.name === "price" ? "price" : c.name === "duplicate" ? "possible duplicate" : "text/contact"}
                      </span>
                    ))}
                  </div>
                )}
                {p.moderation_status === "rejected" && p.moderation_reason && (
                  <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3" data-testid={`reject-reason-${p.id}`}>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-red-700 mb-0.5">Needs a fix</p>
                    <p className="text-xs text-red-800 leading-relaxed">{p.moderation_reason}</p>
                    <p className="text-[11px] text-red-600 mt-1">Edit this listing to fix it — it goes back into review automatically.</p>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                <Link to={`/property/${p.id}`} className="text-sm brand-forest font-semibold hover:underline px-2">View</Link>
                <button
                  onClick={() => nav(`/edit-property/${p.id}`)}
                  data-testid={`edit-${p.id}`}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm border border-brand-subtle hover:border-[#FF5A3D] brand-forest font-semibold"
                >
                  <Pencil size={14} /> Edit
                </button>
                {p.meta_title && (
                  <button
                    onClick={() => revertAi(p.id)}
                    data-testid={`revert-ai-${p.id}`}
                    className="text-xs text-gray-500 hover:text-[#7A1E2A] underline underline-offset-2 px-1"
                  >
                    Revert to original
                  </button>
                )}
                {boostOn && (
                <button
                  onClick={() => setBoostTarget(p)}
                  data-testid={`feature-${p.id}`}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                    p.is_featured
                      ? "bg-[#E0F2FE] text-[#0EA5E9] border border-[#BAE6FD]"
                      : "bg-brand-forest text-white hover:bg-[#E64520]"
                  }`}
                >
                  <Zap size={14} /> {p.is_featured ? "Extend boost" : "Boost"}
                </button>
                )}
                <button
                  onClick={() => del(p.id)}
                  data-testid={`delete-${p.id}`}
                  className="w-10 h-10 rounded-full hover:bg-red-50 flex items-center justify-center text-red-500"
                  aria-label="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              </div>

              {boostOn && !p.is_featured && p.moderation_status === "approved" && (
                <div
                  data-testid={`boost-banner-${p.id}`}
                  className="mt-3 rounded-xl bg-gradient-to-r from-[#FFF1EB] to-[#FFEED9] border border-[#FFD8C7] p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <div className="w-9 h-9 rounded-full bg-[#FF5A3D] text-white flex items-center justify-center shrink-0">
                    <Rocket size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold brand-forest">
                      {user?.boost_trial_used ? "Boost this listing to the top" : "Get a free 7-day boost 🎉"}
                    </p>
                    <p className="text-[12px] text-gray-600">
                      {user?.boost_trial_used
                        ? "Featured listings get up to 5× more views & 3× more inquiries."
                        : "Try boosting free — top of search, Featured badge, 5× more views."}
                    </p>
                  </div>
                  <Button
                    onClick={() => setBoostTarget(p)}
                    data-testid={`boost-banner-cta-${p.id}`}
                    className="bg-brand-forest hover:bg-[#E64520] text-white rounded-full h-9 px-5 text-sm font-semibold shrink-0"
                  >
                    {user?.boost_trial_used ? "Boost now" : "Claim free boost"}
                  </Button>
                </div>
              )}

              {p.is_featured && (
                <div
                  data-testid={`boost-stats-${p.id}`}
                  className="mt-3 rounded-xl bg-gradient-to-r from-[#FFFBEE] to-[#FFF6E0] border border-[#EAD9A6] p-3"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#A87F2E]">
                      <Star size={11} className="fill-[#C9A24A] text-[#C9A24A]" /> Boost performance
                    </span>
                    {p.featured_until && (
                      <span className="text-[11px] text-gray-500 ml-auto">Active until {formatFeaturedUntil(p.featured_until)}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#FFEED9] flex items-center justify-center brand-forest">
                        <Eye size={15} />
                      </div>
                      <div>
                        <p className="font-display text-lg brand-forest leading-none">+{(p.boost_views_gained ?? 0).toLocaleString("en-IN")}</p>
                        <p className="text-[11px] text-gray-500">views since boost</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center text-[#0EA5E9]">
                        <TrendingUp size={15} />
                      </div>
                      <div>
                        <p className="font-display text-lg text-[#0EA5E9] leading-none">+{(p.boost_contacts_gained ?? 0).toLocaleString("en-IN")}</p>
                        <p className="text-[11px] text-gray-500">inquiries since boost</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Boost dialog */}
      <BoostDialog
        property={boostTarget}
        open={!!boostTarget}
        onOpenChange={(o) => !o && setBoostTarget(null)}
      />
    </div>
  );
}
